/**
 * SessionCoordinator — Multi-tab token refresh coordination via BroadcastChannel.
 *
 * Ensures only ONE tab performs a refresh at a time to avoid refresh-token
 * rotation invalidating tokens in sibling tabs.
 *
 * Architecture:
 *   - Each tab announces "I will refresh" via a CLAIM message.
 *   - Other tabs defer for a cooldown period.
 *   - After refresh, the leader broadcasts the new session so siblings
 *     can pick it up from storage without hitting the server.
 */

import { debugLog, debugWarn } from '@/lib/debugLogger';

const CHANNEL_NAME = 'netuno-session-coord';
const CLAIM_COOLDOWN_MS = 12_000; // defer for 12s when another tab claims refresh
const SESSION_UPDATED_COOLDOWN_MS = 5_000; // brief cooldown after receiving updated session

type CoordMessage =
  | { type: 'REFRESH_CLAIM'; tabId: string; timestamp: number }
  | { type: 'REFRESH_DONE'; tabId: string; timestamp: number }
  | { type: 'SESSION_UPDATED'; tabId: string; timestamp: number };

let channelInstance: BroadcastChannel | null = null;

const tabId = typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

let lastExternalClaimAt = 0;
let lastExternalSessionUpdateAt = 0;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (channelInstance) return channelInstance;

  try {
    channelInstance = new BroadcastChannel(CHANNEL_NAME);

    channelInstance.onmessage = (ev: MessageEvent<CoordMessage>) => {
      const msg = ev.data;
      if (!msg || msg.tabId === tabId) return;

      if (msg.type === 'REFRESH_CLAIM') {
        lastExternalClaimAt = msg.timestamp;
        debugLog('auth', 'SessionCoord: outra aba reivindicou refresh', {
          remoteTab: msg.tabId.slice(0, 8),
        });
      } else if (msg.type === 'REFRESH_DONE') {
        debugLog('auth', 'SessionCoord: outra aba completou refresh', {
          remoteTab: msg.tabId.slice(0, 8),
        });
      } else if (msg.type === 'SESSION_UPDATED') {
        lastExternalSessionUpdateAt = msg.timestamp;
        debugLog('auth', 'SessionCoord: sessão atualizada por outra aba', {
          remoteTab: msg.tabId.slice(0, 8),
        });
      }
    };

    channelInstance.onmessageerror = () => {
      debugWarn('auth', 'SessionCoord: erro ao deserializar mensagem');
    };
  } catch {
    // BroadcastChannel not available (e.g. some privacy browsers)
  }

  return channelInstance;
}

/**
 * Returns true if THIS tab should perform the refresh.
 * Returns false if another tab recently claimed it — caller should skip.
 */
export function claimRefreshLeadership(): boolean {
  const now = Date.now();

  // Another tab claimed recently — defer
  if (now - lastExternalClaimAt < CLAIM_COOLDOWN_MS) {
    debugLog('auth', 'SessionCoord: refresh deferido — outra aba é líder', {
      msSinceClaim: now - lastExternalClaimAt,
    });
    return false;
  }

  // Another tab just updated the session — no need to refresh
  if (now - lastExternalSessionUpdateAt < SESSION_UPDATED_COOLDOWN_MS) {
    debugLog('auth', 'SessionCoord: refresh deferido — sessão recém atualizada por outra aba');
    return false;
  }

  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage({ type: 'REFRESH_CLAIM', tabId, timestamp: now } satisfies CoordMessage);
    } catch {
      // channel may be closed
    }
  }

  return true;
}

/** Announce that this tab finished a successful refresh. */
export function announceRefreshDone(): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    const now = Date.now();
    ch.postMessage({ type: 'REFRESH_DONE', tabId, timestamp: now } satisfies CoordMessage);
    ch.postMessage({ type: 'SESSION_UPDATED', tabId, timestamp: now } satisfies CoordMessage);
  } catch {
    // no-op
  }
}

/** Check if another tab recently updated the session (no need for this tab to refresh). */
export function wasSessionRecentlyUpdatedByPeer(): boolean {
  return Date.now() - lastExternalSessionUpdateAt < SESSION_UPDATED_COOLDOWN_MS;
}

/** Cleanup on unmount. */
export function destroySessionCoordinator(): void {
  if (channelInstance) {
    try {
      channelInstance.close();
    } catch {
      // no-op
    }
    channelInstance = null;
  }
}
